import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import OnTraccrSwitch from '../../../../common/inputs/OnTraccrSwitch';
import { updateIndividualProject } from '../../../state/projects.actions';
import CostTrackingBudgetProgressTable from './CostTrackingBudgetProgressTable';
import Permissions from '../../../../auth/Permissions';
import { getProjectIdMap } from '../../../../state/selectors';

export default function CostTrackingBudgetProgress({
  unphasedData,
  phases,
  onUploadInvoice,
  onHistoryClick,
  projectId,
  averageWage = 0,
  hiddenColumnKeys,
}) {
  const dispatch = useDispatch();

  const hasWrite = Permissions.has('PROJECT_PROGRESS_WRITE');

  const projectMap = useSelector(getProjectIdMap);
  const ourProject = useMemo(() => projectMap[projectId], [projectMap, projectId]);

  const { enableBillingDisplay } = ourProject ?? {};

  const [billingToggleLoading, setBillingToggleLoading] = useState(false);

  // Tree: phase → costcode rows (enriched) → sub-category rows (children of costcode)
  const phasedData = useMemo(() => (
    (phases || []).map((phase) => {
      const costcodes = phase.costcodes ?? [];
      return {
        rowKey: `phase-${phase.id}`,
        name: phase.name,
        description: phase.description,
        isPhaseRow: true,
        ...(costcodes.length > 0 ? { children: costcodes } : {}),
        id: phase.id,
        phaseId: phase.id,
      };
    })
  ), [phases]);

  const toggleBillingDisplay = useCallback(async () => {
    setBillingToggleLoading(true);
    await dispatch(updateIndividualProject({
      projectId,
      payload: {
        details: {
          enableBillingDisplay: !enableBillingDisplay,
        },
      },
    }));
    setBillingToggleLoading(false);
  }, [enableBillingDisplay, projectId]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {!!hasWrite && (
        <OnTraccrSwitch
          value={!!enableBillingDisplay}
          title="Display billing"
          onChange={toggleBillingDisplay}
          disabled={billingToggleLoading}
          loading={billingToggleLoading}
        />
      )}
      <div>
        <span className="budget-tracking-section-title">
          Unphased
        </span>
        <CostTrackingBudgetProgressTable
          dataSource={unphasedData}
          onUploadInvoice={onUploadInvoice}
          onHistoryClick={onHistoryClick}
          showBilling={!!enableBillingDisplay}
          averageWage={averageWage}
          hiddenColumnKeys={hiddenColumnKeys}
        />
      </div>
      <div>
        <span className="budget-tracking-section-title">
          Phased
        </span>
        <CostTrackingBudgetProgressTable
          dataSource={phasedData}
          onUploadInvoice={onUploadInvoice}
          onHistoryClick={onHistoryClick}
          showBilling={!!enableBillingDisplay}
          hiddenColumnKeys={hiddenColumnKeys}
        />
      </div>
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
CostTrackingBudgetProgress.propTypes = {
  unphasedData: PropTypes.array,
  phases: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
  })),
  onUploadInvoice: PropTypes.func,
  onHistoryClick: PropTypes.func,
  projectId: PropTypes.string,
  averageWage: PropTypes.number,
  hiddenColumnKeys: PropTypes.instanceOf(Set),
};

CostTrackingBudgetProgress.defaultProps = {
  unphasedData: [],
  phases: null,
  onUploadInvoice: null,
  onHistoryClick: null,
  projectId: null,
  averageWage: 0,
  hiddenColumnKeys: undefined,
};
